// @flow
/* eslint arrow-parens:0 */

import * as React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import type { Dispatch } from 'redux';
import type { State as Experiences } from 'app/common/redux/reducers/experiences';
import {
  experienceFetch,
  placementExperimentsTrigger,
  placementMount,
  placementUnmount,
} from 'app/common/redux/actions/experiences';
import {
  type Experience,
  ExperienceProps,
  isRenderableExperience,
} from 'app/common/lib/react/experiences/experienceUtils';

type Props = {|
  dispatch: Dispatch<any>,
  experience: Experience,
|};

type State = {|
  extraContext?: () => void | {},
|};

type WithPlacementConfiguration = {
  shouldUsePlacedExperience?: boolean,
  dispatchMountActionDuringSSR?: boolean,
};

type AddedProps = { experience: ?Experience | void };

// separating out the function for testing
type MapStateToProps<OwnProps> = (
  state: { experiences: Experiences },
  ownProps: OwnProps,
) => {| experience: Experience |};

export function getMapStateToProps<T>(placementId: number): MapStateToProps<T> {
  return (state) => {
    const existingExperience = state.experiences.eligibleExperiences[placementId];
    return { experience: existingExperience };
  };
}

// separating out the class for testing
export function getWrapperComponent<T>(
  placementId: number,
  extraContext: Function | Object,
  // There are certain uses cases where we dont want to return the placedExperience, such as mweb nux
  shouldUsePlacedExperience: boolean = true,
  // Dispatch the placement mount to redux early; this enables the server to fetch and render experience data server side
  dispatchMountActionDuringSSR: boolean = true,
  WrappedComponent: React.ComponentType<T>,
): React.ComponentType<T & Props> {
  return class Placement extends React.Component<Props, State> {
    static propTypes = {
      dispatch: PropTypes.func.isRequired,
      experience: PropTypes.shape(ExperienceProps),
    };

    constructor(props) {
      super(props);

      if (__SERVER__ && dispatchMountActionDuringSSR) {
        this.dispatchPlacementMount(true);
      }
    }

    state: State = {
      extraContext: undefined,
    };

    componentDidMount() {
      this.dispatchPlacementMount(false);
    }

    UNSAFE_componentWillUpdate(nextProps: Props, nextState: State) {
      const { dispatch, experience } = nextProps;
      let newExtraContext = extraContext;
      if (typeof extraContext === 'function') {
        const extraContextState = nextState.extraContext || {};
        newExtraContext = extraContext(nextProps, extraContextState);
      }
      // Fetch new experiences if we don't currently have one and the extra context could be different
      if (!isRenderableExperience(experience) && typeof extraContext === 'function') {
        if (newExtraContext && Object.keys(newExtraContext).length) {
          const oldExtraContext = extraContext(this.props, this.state);
          // quick comparison check to avoid looping through the entire objects
          if (JSON.stringify(oldExtraContext) !== JSON.stringify(newExtraContext)) {
            dispatch(experienceFetch(placementId, newExtraContext));
          }
        }
      }

      // if we received a new experience, it would not have been triggered yet
      if (experience && (!this.props.experience || experience.id !== this.props.experience.id)) {
        dispatch(placementExperimentsTrigger(placementId, newExtraContext));
      }
    }

    componentWillUnmount() {
      const { dispatch } = this.props;
      dispatch(placementUnmount(placementId));
    }

    setExtraContextState = (extraContext: Object) => this.setState({ extraContext });

    dispatchPlacementMount(isServerSideRender) {
      const { dispatch } = this.props;
      let extraContextObject = {};
      if (typeof extraContext === 'function') {
        extraContextObject = extraContext(this.props, this.state);
      } else {
        extraContextObject = Object.assign({}, extraContext);
      }
      dispatch(placementMount(placementId, extraContextObject, isServerSideRender));
    }

    render() {
      const { experience } = this.props;
      // only pass down experiences if they're not NOOP
      return (
        // $FlowFixMe: New error after Flow Upgrade http://pinch.pinadmin.com/flow53upgdoc
        <WrappedComponent
          {...this.props}
          setPlacementExtraContext={this.setExtraContextState}
          experience={isRenderableExperience(experience) ? experience : null}
        />
      );
    }
  };
}

const withPlacementDefaultConfiguration: WithPlacementConfiguration = {
  shouldUsePlacedExperience: true,
  dispatchMountActionDuringSSR: false,
};

export default (
  placementId: number,
  extraContext: Function | Object = {},
  configuration?: WithPlacementConfiguration,
) => <InputProps: {}>(
  WrappedComponent: React.AbstractComponent<InputProps>,
): React.AbstractComponent<$Diff<InputProps, AddedProps>> => {
  const mapStateToProps: MapStateToProps<InputProps> = getMapStateToProps(placementId);

  configuration = Object.assign({}, withPlacementDefaultConfiguration, configuration);

  const WrapperComponent = getWrapperComponent(
    placementId,
    extraContext,
    configuration.shouldUsePlacedExperience,
    configuration.dispatchMountActionDuringSSR,
    WrappedComponent,
  );
  return connect(mapStateToProps)(WrapperComponent);
};
